import { nanoid } from 'nanoid'
import { create } from 'zustand'

export const newClientId = () => `msg-${nanoid(6)}`

const coerceClientId = message =>
  message.clientId ?? message.id ?? newClientId()

/**
 * Handles streaming and loading messages for the current conversation.
 * @returns {Object}  The store
 */
export const useCurrentConversationStore = create((set, get) => ({
  id: null,
  messages: [],
  streamPhase: 'idle',
  agentStatus: 'idle',
  activeBlock: '',
  // NOTE: `completedBlocks` was used by a legacy manual streaming renderer.
  // We keep it for backward compatibility, but it is no longer used by the UI.
  completedBlocks: [],
  pagination: {
    count: 0,
    limit: 25,
    offset: 0,
    total: null,
  },
  loading: false,
  toastMessage: null,
  hasPendingUploads: false,
  getMessageByClientId: clientId =>
    get().messages.find(m => m.clientId === clientId),
  setHasPendiningUploads: hasPendingUploads => set({ hasPendingUploads }),
  setCurrentId: id => set({ id }),
  setLoading: loading => set({ loading }),
  setCurrentConversation: (messages, pagination) =>
    set({
      messages,
      pagination: pagination || {
        count: messages.length,
        limit: messages.length,
        offset: 0,
        total: messages.length,
      },
    }),
  setMessages: messages => set({ messages }),
  addUserMessage: message =>
    set(s => ({
      messages: [
        ...s.messages,
        {
          ...message,
          clientId: message.clientId ?? newClientId(),
        },
      ],
    })),
  addStreamingAssistant: () => {
    const cId = newClientId()
    set(s => ({
      messages: [
        ...s.messages,
        {
          clientId: cId,
          id: null,
          role: 'assistant',
          content: '',
        },
      ],
    }))
    return cId
  },
  patchAssistant: (clientId, patch) =>
    //  Updates the assistant message in global state with full content
    //  Since clientId is the key, and it is not changed
    //  AgentMessage component will not re-render
    set(s => ({
      messages: s.messages.map(m =>
        m.clientId === clientId ? { ...m, ...patch } : m,
      ),
    })),
  patchLastAsError: msg =>
    set(s => {
      const msgs = [...s.messages]
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'assistant') {
          msgs[i] = { ...msgs[i], content: msg }
          break
        }
      }
      return { messages: msgs }
    }),
  appendMessages: newMessages =>
    set(state => ({ messages: [...state.messages, ...newMessages] })),
  setToastMessage: message => set({ toastMessage: message }),
  setActiveBlock: activeBlock => set({ activeBlock }),
  // Legacy streaming helpers (currently unused by the Streamdown-based renderer)
  setCompletedBlocks: completedBlocks => set({ completedBlocks }),
  setPagination: pagination => set({ pagination }),
  addCompletedBlock: block =>
    set(state => ({
      completedBlocks: [...state.completedBlocks, block],
    })),
  setStreamPhase: streamPhase => set({ streamPhase }),
  setAgentStatus: agentStatus => set({ agentStatus }),
  updateLastAgentMessage: content =>
    set(state => {
      const messages = [...state.messages]
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'assistant') {
          messages[i] = { ...messages[i], content }
          break
        }
      }
      return { messages }
    }),
  updateMessage: async (api, messageId, data) => {
    return api
      .updateMessage(messageId, data)
      .then(({ body: updatedMessage }) => {
        set({
          messages: get().messages.map(m =>
            m.id === messageId ? { ...m, ...updatedMessage } : m,
          ),
        })
        return updatedMessage
      })
      .catch(err => {
        throw err
      })
  },

  loadConversation: async (
    api,
    convoId,
    query = {},
    options = {},
    onError = () => {},
    force = false,
  ) => {
    const currentId = get().id
    const messages = get().messages
    if (currentId === convoId && messages.length > 0 && !force) {
      // messages check required in case of a race condition
      // on browser refresh, the conversationId is set but the messages are not yet loaded
      // or if called while streaming the current conversation
      return
    }
    set({ loading: true, id: convoId, messages: [] })
    const pagination = get().pagination || {}
    const limit = query.limit ?? pagination.limit ?? 25
    const offset = query.offset ?? pagination.offset ?? 0
    try {
      const res = await api.getConversationMessages(
        convoId,
        {
          limit,
          offset,
        },
        options,
      )
      set({
        messages: res.body.data.reverse().map(m => ({
          ...m,
          clientId: coerceClientId(m),
        })),
        pagination: {
          count: res.body.count,
          limit: res.body.limit,
          offset: res.body.offset,
          total: res.body.total,
        },
        loading: false,
        id: convoId,
      })
    } catch (err) {
      set({ loading: false })
      onError(err)
    }
  },
  clearCurrentConversation: () => set({ id: null, messages: [] }),
}))
